import {type ThunkDispatch, type UnknownAction} from '@reduxjs/toolkit';
import {showNotification, useEventListener} from 'platform/components';

import {useCallback, useRef, useState} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isEmpty, isNotNil, omit, pick} from 'ramda';

import {
  newVehicleApi,
  usePutTrimFeatureAssignmentsMutation,
  type FeatureResponseBody,
  type TrimFeatureAssignment,
  type TrimFeatureAssignmentInput,
} from '@dms/api/new-vehicle';

import type {ApiError} from 'shared/web';

import {handleCatalogueError, t} from './catalogueUi';
import {setTrimInclusion} from './setTrimInclusion';
import {type CatalogueState} from './useMatrixSaveQueue';

export type MatrixTrim = {id: string; name: string; version: number};
export type CellValue = TrimFeatureAssignmentInput['inclusion'] | 'NOT_OFFERED';
/** The last cell the author changed, so the matrix can announce it. */
type CellChange = {
  trim: MatrixTrim;
  feature: FeatureResponseBody;
  value: CellValue;
  isUnsaved: boolean;
};
type ReplaceAssignments = (
  trimId: string,
  update: (assignments: TrimFeatureAssignment[]) => TrimFeatureAssignmentInput[]
) => Promise<TrimFeatureAssignment[]>;
/** Per trim id, per feature id: a value the author picked that is not saved yet. */
type Drafts = Record<string, Record<string, CellValue>>;

/**
 * The equipment-by-trim matrix's unsaved cell values and the writes that save them. Saves run one
 * at a time across the whole matrix and each trim's save starts from a fresh read, so a value
 * cycled through Not offered never deletes an assignment's price and conditions.
 */
export function useEquipmentMatrixDrafts(props: {
  trims: MatrixTrim[];
  features: FeatureResponseBody[];
}) {
  const store = useStore<CatalogueState>();
  const dispatch = useDispatch<ThunkDispatch<CatalogueState, unknown, UnknownAction>>();
  const [putAssignments] = usePutTrimFeatureAssignmentsMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  // Every assignment write of the matrix, one at a time: the backend rejects a trim's PUT as stale
  // while another trim of the model year is being written (DMS-NV-CT-0012).
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  // Per trim, the drafts its running save is writing. The cache holds the values from before that
  // save until it confirms them, so meanwhile a click on one of those cells always stays a draft.
  const savingDrafts = useRef(new Map<string, Record<string, CellValue>>());
  const [allDrafts, setDrafts] = useState<Drafts>({});
  const [isSaving, setSaving] = useState(false);
  const [lastChange, setLastChange] = useState<CellChange | null>(null);
  // A trim or equipment item archived meanwhile drops out of the matrix, and so do its drafts.
  const featureIds = props.features.map(({id}) => id);
  const drafts: Drafts = Object.fromEntries(
    props.trims.map(({id}) => [id, pick(featureIds, allDrafts[id] ?? {})])
  );
  const draftCount = Object.values(drafts).flatMap(Object.keys).length;
  useEventListener(window, 'beforeunload', (event) => {
    if (draftCount > 0) {
      event.preventDefault();
    }
  });

  // replaceAssignments and setDraft keep their identity so a click re-renders only its own cell:
  // re-rendering all 630 cells of a 63 x 10 matrix took about 0.7 s per click (dev build).
  const replaceAssignments = useCallback<ReplaceAssignments>(
    (trimId, update) => {
      const operation: Promise<TrimFeatureAssignment[]> = writes.current
        .catch(() => undefined)
        .then(async () => {
          const trim = await getTrim({trimId}, false).unwrap();
          await getAssignments({trimId}, true);
          const assignments = await getAssignments({trimId}, false).unwrap();
          return putAssignments({
            trimId,
            replaceTrimFeatureAssignmentsRequestBody: {
              assignments: update(assignments),
              version: trim.version,
            },
          }).unwrap();
        });
      writes.current = operation;
      return operation;
    },
    [getTrim, getAssignments, putAssignments]
  );

  const setDraft = useCallback(
    (trim: MatrixTrim, feature: FeatureResponseBody, value: CellValue, serverValue: CellValue) => {
      const isUnsaved =
        value !== serverValue || isNotNil(savingDrafts.current.get(trim.id)?.[feature.id]);
      setDrafts((current) => {
        const trimDrafts = omit([feature.id], current[trim.id] ?? {});
        return {
          ...current,
          [trim.id]: isUnsaved ? {...trimDrafts, [feature.id]: value} : trimDrafts,
        };
      });
      setLastChange({trim, feature, value, isUnsaved});
    },
    []
  );

  const saveTrim = (trimId: string, trimDrafts: Record<string, CellValue>) => {
    savingDrafts.current.set(trimId, trimDrafts);
    return replaceAssignments(trimId, (freshAssignments) =>
      Object.entries(trimDrafts).reduce<TrimFeatureAssignmentInput[]>(
        (assignments, [featureId, value]) =>
          value === 'NOT_OFFERED'
            ? assignments.filter((assignment) => assignment.featureId !== featureId)
            : setTrimInclusion(assignments, featureId, value),
        freshAssignments
      )
    )
      .then((saved) => {
        // The saved set goes into the cache before the drafts retire, so no cell flickers back.
        dispatch(
          newVehicleApi.util.updateQueryData('listTrimFeatureAssignments', {trimId}, () => saved)
        );
        // Cells stay clickable during the save: keep any value changed since it started.
        setDrafts((current) => ({
          ...current,
          [trimId]: Object.fromEntries(
            Object.entries(current[trimId] ?? {}).filter(
              ([featureId, value]) => trimDrafts[featureId] !== value
            )
          ),
        }));
        return true;
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error);
        // A cell cycled back to its saved value during the failed save is not a change any more.
        const stored =
          newVehicleApi.endpoints.listTrimFeatureAssignments.select({trimId})(store.getState())
            .data ?? [];
        const storedValue = (featureId: string): CellValue =>
          stored.find((assignment) => assignment.featureId === featureId)?.inclusion ??
          'NOT_OFFERED';
        setDrafts((current) => ({
          ...current,
          [trimId]: Object.fromEntries(
            Object.entries(current[trimId] ?? {}).filter(
              ([featureId, value]) => storedValue(featureId) !== value
            )
          ),
        }));
        return false;
      })
      .finally(() => savingDrafts.current.delete(trimId));
  };

  const save = async () => {
    // The pending bar and the leave dialog can both ask to save; only one save runs at a time.
    if (isSaving) {
      return false;
    }
    setSaving(true);
    const results = await Promise.all(
      Object.entries(drafts)
        .filter(([, trimDrafts]) => !isEmpty(trimDrafts))
        .map(([trimId, trimDrafts]) => saveTrim(trimId, trimDrafts))
    );
    setSaving(false);
    const isEverySaved = results.every(Boolean);
    if (isEverySaved) {
      showNotification.success(
        t('entity.newVehicle.catalogue.assignmentsSaved', 'Trim assignments saved')
      );
    }
    return isEverySaved;
  };

  return {
    drafts,
    draftCount,
    isSaving,
    lastChange,
    setDraft,
    replaceAssignments,
    save,
    discard: () => setDrafts({}),
  };
}
